import { create } from 'zustand';
import { ParticipantRole } from '@/consts/meeting.ts';
import { combine } from 'zustand/middleware';
import Api from '@/api/api.ts';
import { AccountRole } from '@/types/api.ts';

interface UserState {
  userUid: string;
  userName: string;
  isAuthenticated: boolean;
  isSuperuser: boolean;
  // becomes true once the initial /auth/users/me probe has resolved (success OR
  // failure) — used by ProtectedRoute to avoid a flash-redirect before we know.
  hydrated: boolean;
  // authenticated account identity (separate from the per-meeting role below)
  email: string;
  accountRole: AccountRole | undefined;
  authUserId: string;
  authName: string;
  avatarUrl: string | null;
  role: ParticipantRole | undefined;
  participantActiveCanvas: 'canvasFile' | 'canvasBoard';
  // Which source the top ("Stage") pane shows. Per-viewer, independent of the
  // board pane below. 'document' = the PDF viewer, 'screen' = an active share.
  stageSource: 'document' | 'screen';
}

interface UserActions {
  setUserUid: (userUid: string) => void;
  setUserName: (userName: string) => void;
  setIsAuthenticated: (isAuthenticated: boolean) => void;
  setRole: (role: ParticipantRole | undefined) => void;
  setParticipantActiveCanvas: (canvas: 'canvasFile' | 'canvasBoard') => void;
  setStageSource: (source: 'document' | 'screen') => void;
  resetMeetingIdentity: () => void;
  // probe the auth cookie and populate account state; safe to call on every load
  hydrate: () => Promise<void>;
  // re-read the account here and in the other open tabs (after a profile edit)
  refreshAccount: () => Promise<void>;
  logout: () => Promise<void>;
}

// Each tab keeps its own copy of the account. A profile edit in one tab tells
// the others to re-read it, so e.g. a running meeting picks up the new name.
const accountChannel =
  typeof window !== 'undefined' && typeof BroadcastChannel !== 'undefined'
    ? new BroadcastChannel('account')
    : null;

export const useUserStore = create(
  combine<UserState, UserActions>(
    {
      userUid: '',
      userName: '',
      isAuthenticated: false,
      isSuperuser: false,
      hydrated: false,
      email: '',
      accountRole: undefined,
      authUserId: '',
      authName: '',
      avatarUrl: null,
      role: undefined,
      participantActiveCanvas: 'canvasFile',
      stageSource: 'document'
    },
    (set) => ({
      setUserUid: (userUid) => set({ userUid }),
      setUserName: (userName) => set({ userName }),
      setIsAuthenticated: (isAuthenticated) => set({ isAuthenticated }),
      setRole: (role) => set({ role }),
      setParticipantActiveCanvas: (participantActiveCanvas) => set({ participantActiveCanvas }),
      setStageSource: (stageSource) => set({ stageSource }),
      resetMeetingIdentity: () =>
        set({
          userUid: '',
          role: undefined,
          participantActiveCanvas: 'canvasFile',
          stageSource: 'document'
        }),
      hydrate: async () => {
        try {
          const user = await Api.probeCurrentUser();
          if (!user) {
            set({
              isAuthenticated: false,
              isSuperuser: false,
              email: '',
              accountRole: undefined,
              authUserId: '',
              authName: '',
              avatarUrl: null,
              hydrated: true
            });
            return;
          }
          set({
            isAuthenticated: true,
            isSuperuser: Boolean(user.is_superuser),
            email: user.email,
            accountRole: user.role,
            authUserId: user.id,
            authName: user.name || user.email,
            avatarUrl: user.avatar_url,
            hydrated: true
          });
        } catch {
          set({
            isAuthenticated: false,
            isSuperuser: false,
            email: '',
            accountRole: undefined,
            authUserId: '',
            authName: '',
            avatarUrl: null,
            hydrated: true
          });
        }
      },
      refreshAccount: async () => {
        await useUserStore.getState().hydrate();
        accountChannel?.postMessage('changed');
      },
      logout: async () => {
        try {
          await Api.logout();
        } catch {
          // ignore network errors on logout — clear local state regardless
        }
        set({
          userUid: '',
          userName: '',
          isAuthenticated: false,
          isSuperuser: false,
          email: '',
          accountRole: undefined,
          authUserId: '',
          authName: '',
          avatarUrl: null,
          role: undefined,
          participantActiveCanvas: 'canvasFile',
          stageSource: 'document'
        });
      }
    })
  )
);

if (accountChannel) {
  accountChannel.onmessage = () => {
    void useUserStore.getState().hydrate();
  };
}
